import { useState, useCallback, useEffect } from 'react';
import { useUserContext } from '../../modules/Users/UserContext';
import { Button, Icon, Input } from 'lynkco-up-core';

import {
  getOwnedDealerships,
  addUserToDealershipForOwner,
  removeUserFromDealershipForOwner,
  removeVinFromDealershipForOwner,
  addVinToDealershipForOwner,
} from '../../services/dealerships';
import {
  AddUserToDealershipForOwnerReqV1,
  AddVinToDealershipForOwnerReqV1,
  CeDealershipRespV1,
  CeDealershipV1,
  DealershipUserV1,
  DealershipVehicleV1,
  GetDealershipQuery,
} from '../../services/dealerships/types';

import Dealership from './Dealership';

const searchInputs = [
  { id: 'search_userId', name: 'userId', placeholder: 'Search by User ID...', icon: 'person' },
  { id: 'search_email', name: 'email', placeholder: 'Search by Email...', icon: 'email' },
] as const;

const addUserInputsAdmin = [
  { id: 'addUser_dealershipId', name: 'dealershipId', placeholder: 'Dealership ID', icon: 'store' },
  { id: 'addUser_ownerUserid', name: 'ownerUserid', placeholder: 'Owner User ID', icon: 'supervisor_account' },
  { id: 'addUser_userId', name: 'userId', placeholder: 'User ID to add', icon: 'person_add' },
  { id: 'addUser_userEmail', name: 'userEmail', placeholder: 'User Email to add', icon: 'alternate_email' },
] as const;

const addVinInputsAdmin = [
  { id: 'addVin_dealershipId', name: 'dealershipId', placeholder: 'Dealership ID', icon: 'store' },
  { id: 'addVin_ownerUserid', name: 'ownerUserid', placeholder: 'Owner User ID', icon: 'supervisor_account' },
  { id: 'addVin_vin', name: 'vin', placeholder: 'VIN to add', icon: 'directions_car' },
] as const;

const addUserInputsUser = [
  { id: 'addUser_userId', name: 'userId', placeholder: 'User ID to add', icon: 'person_add' },
  { id: 'addUser_userEmail', name: 'userEmail', placeholder: 'User Email to add', icon: 'alternate_email' },
] as const;

const addVinInputsUser = [
  { id: 'addVin_vin', name: 'vin', placeholder: 'VIN to add', icon: 'directions_car' },
] as const;

function Dealerships() {
  const { user, checkAccess } = useUserContext();

  const [dealershipResponse, setDealershipResponse] = useState<CeDealershipRespV1>();
  const [searchQuery, setSearchQuery] = useState<Partial<GetDealershipQuery>>({ email: user.username });
  const [addUserForm, setAddUserForm] = useState<Partial<AddUserToDealershipForOwnerReqV1>>({});
  const [addVinForm, setAddVinForm] = useState<Partial<AddVinToDealershipForOwnerReqV1>>({});

  const fetchDealerships = useCallback(async () => {
    if (!searchQuery.email && !searchQuery.userId) {
      console.warn('Search query is empty. Fetching aborted.');
      return;
    }
    try {
      const data = await getOwnedDealerships(searchQuery);
      setDealershipResponse(data);
      setAddUserForm(prev => ({
        ...prev,
        dealershipId: data?.dealerships?.at(0)?.id,
        ownerUserid: data?.dealerships?.at(0)?.owner?.id,
      }));
      setAddVinForm(prev => ({
        ...prev,
        dealershipId: data?.dealerships?.at(0)?.id,
        ownerUserid: data?.dealerships?.at(0)?.owner?.id,
      }));
    } catch (error) {
      console.error('Failed to fetch dealerships:', error);
    }
  }, [searchQuery]);

  const handleSearchChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;
    setSearchQuery(prev => ({ ...prev, [name]: value }));
  };

  const handleAddUserFormChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;
    setAddUserForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAddVinFormChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;
    setAddVinForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAddUser = async () => {
    if (!addUserForm.dealershipId || !addUserForm.ownerUserid || (!addUserForm.userId && !addUserForm.userEmail)) {
      alert('Please fill all required fields to add a user.');
      return;
    }
    try {
      await addUserToDealershipForOwner(addUserForm as AddUserToDealershipForOwnerReqV1);
      fetchDealerships(); // Refresh data
    } catch (error) {
      console.error('Failed to add user:', error);
    }
  };

  const handleAddVin = async () => {
    if (!addVinForm.dealershipId || !addVinForm.ownerUserid || !addVinForm.vin) {
      alert('Please fill all required fields to add a vin.');
      return;
    }
    try {
      await addVinToDealershipForOwner(addVinForm as AddVinToDealershipForOwnerReqV1);
      fetchDealerships(); // Refresh data
    } catch (error) {
      console.error('Failed to add user:', error);
    }
  };

  const handleRemoveUser = async (userToRemove: DealershipUserV1, dealership: CeDealershipV1) => {
    if (!dealership.owner?.id) {
      console.error('Cannot remove user: Dealership owner is missing.');
      return;
    }
    try {
      await removeUserFromDealershipForOwner({
        ownerUserid: dealership.owner.id,
        dealershipId: dealership.id,
        userId: userToRemove.id,
      });
      fetchDealerships(); // Refresh data
    } catch (error) {
      console.error('Failed to remove user:', error);
    }
  };

  const handlePrefillAddUserForm = (dealership: CeDealershipV1) => {
    setAddUserForm(prev => ({
      ...prev,
      dealershipId: dealership.id,
      ownerUserid: dealership.owner?.id,
    }));
    setAddVinForm(prev => ({
      ...prev,
      dealershipId: dealership.id,
      ownerUserid: dealership.owner?.id,
    }));
  };

  const handleUserSelectForForm = (selectedUser: DealershipUserV1) => {
    setAddUserForm(prev => ({
      ...prev,
      userId: selectedUser.id,
      userEmail: selectedUser.email,
    }));
  };
  const handleRemoveVehicle = (vehicle: DealershipVehicleV1, dealership: CeDealershipV1) => {
    if (!dealership.owner?.id) {
      console.error('Cannot remove user: Dealership owner is missing.');
      return;
    }
    try {
      removeVinFromDealershipForOwner({
        ownerUserid: dealership?.owner?.id,
        dealershipId: dealership?.id,
        vin: vehicle?.vin,
      });
      fetchDealerships(); // Refresh data
    } catch (error) {
      console.error('Failed to remove user:', error);
    }
  };
  const getUserInput = () => {
    if (checkAccess('DEALERSHIP_ADMINISTRATOR')) {
      return addUserInputsAdmin.map(({ id, name, placeholder, icon }) => (
        <Input
          key={id}
          id={id}
          name={name} // Use name to identify the field
          placeholder={placeholder}
          leadingIcon={icon}
          value={addUserForm[name] || ''}
          onChange={handleAddUserFormChange} // Use the dedicated handler
          extraClasses="mb-3"
        />
      ));
    } else if (checkAccess('DEALERSHIP_USER')) {
      return addUserInputsUser.map(({ id, name, placeholder, icon }) => (
        <Input
          key={id}
          id={id}
          name={name} // Use name to identify the field
          placeholder={placeholder}
          leadingIcon={icon}
          value={addUserForm[name] || ''}
          onChange={handleAddUserFormChange} // Use the dedicated handler
          extraClasses="mb-3"
        />
      ));
    }
  };
  const getVinInput = () => {
    if (checkAccess('DEALERSHIP_ADMINISTRATOR')) {
      return addVinInputsAdmin.map(({ id, name, placeholder, icon }) => (
        <Input
          key={id}
          id={id}
          name={name} // Use name to identify the field
          placeholder={placeholder}
          leadingIcon={icon}
          value={addVinForm[name] || ''}
          onChange={handleAddVinFormChange} // Use the dedicated handler
          extraClasses="mb-3"
        />
      ));
    } else if (checkAccess('DEALERSHIP_USER')) {
      return addVinInputsUser.map(({ id, name, placeholder, icon }) => (
        <Input
          key={id}
          id={id}
          name={name} // Use name to identify the field
          placeholder={placeholder}
          leadingIcon={icon}
          value={addVinForm[name] || ''}
          onChange={handleAddVinFormChange} // Use the dedicated handler
          extraClasses="mb-3"
        />
      ));
    }
  };
  useEffect(() => {
    fetchDealerships();
  }, []);
  return (
    <div>
      <div className="p-7">
        <div className="flex flex-wrap max-w-6xl">
          {/* Search Dealerships Section */}
          {checkAccess('DEALERSHIP_ADMINISTRATOR') && (
            <div className="basis-full md:basis-1/2 lg:basis-1/3 pr-6 mb-6">
              <h3 className="font-medium text-base">Find Dealerships</h3>
              <div className="mt-4">
                {searchInputs.map(({ id, name, placeholder, icon }) => (
                  <Input
                    key={id}
                    id={id}
                    name={name} // Use name to identify the field
                    placeholder={placeholder}
                    leadingIcon={icon}
                    value={searchQuery[name] || ''}
                    onChange={handleSearchChange} // Use the dedicated handler
                    extraClasses="mb-3"
                  />
                ))}
              </div>
              <Button onClick={fetchDealerships}>
                Fetch Dealership
                <span className="ml-2 flex items-center">
                  <Icon name="search" size="small" variant="light" padding={0} />
                </span>
              </Button>
            </div>
          )}

          {/* Add User to Dealership Section */}
          <div className="basis-full md:basis-1/2 lg:basis-1/3 pr-6 mb-6">
            <h3 className="font-medium text-base">Add User to Dealership</h3>
            <div className="mt-4">{getUserInput()}</div>
            <Button onClick={handleAddUser} extraClasses="ml-2">
              Add User
              <span className="ml-2 flex items-center">
                <Icon name="add" size="small" variant="light" padding={0} />
              </span>
            </Button>
          </div>

          {/* Add VIN to Dealership Section */}
          <div className="basis-full md:basis-1/2 lg:basis-1/3 pr-6 mb-6">
            <h3 className="font-medium text-base">Add VIN to Dealership</h3>
            <div className="mt-4">{getVinInput()}</div>
            <Button onClick={handleAddVin} extraClasses="ml-2">
              Add VIN
              <span className="ml-2 flex items-center">
                <Icon name="add" size="small" variant="light" padding={0} />
              </span>
            </Button>
          </div>
        </div>
      </div>
      <div className="p-4">
        <Dealership
          dealerships={dealershipResponse?.dealerships}
          onDealershipClick={handlePrefillAddUserForm}
          onUserClickDelete={handleRemoveUser}
          onUserSelect={handleUserSelectForForm}
          onOwnerSelect={handleUserSelectForForm}
          onVehicleClickDelete={handleRemoveVehicle}
        />
      </div>
    </div>
  );
}

export default Dealerships;
